import type {
  OptimizerRunV2State,
  OptimizerRunV2Type,
} from '@generated/graphql';
import { MenuItem } from '@ovComponents/2-component/menuItem/menuItem';
import { SelectField } from '@ovComponents/2-component/selectField/selectField';
import { Box, Button, Typography, useTheme } from 'hephaestus/web';
import { useTranslation } from 'react-i18next';
import { BULK_REBALANCE_START_RUN_TYPES } from '../constants/policy';
import { TERMINAL_RUN_STATES } from '../utils/presentation/runProgress';

export type BulkRebalanceListFilters = {
  type?: OptimizerRunV2Type;
  state?: OptimizerRunV2State;
};

export type BulkRebalanceFiltersProps = {
  filters: BulkRebalanceListFilters;
  onTypeChange: (value?: OptimizerRunV2Type) => void;
  onStateChange: (value?: OptimizerRunV2State) => void;
};

export const BulkRebalanceFilters = ({
  filters,
  onTypeChange,
  onStateChange,
}: BulkRebalanceFiltersProps) => {
  const { t } = useTranslation(['bulkRebalance']);
  const theme = useTheme();
  const hasActiveFilters = Boolean(filters.type || filters.state);

  const clearFilters = () => {
    onTypeChange(undefined);
    onStateChange(undefined);
  };

  return (
    <Box
      data-testid='bulk-rebalance-filters'
      sx={{
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 1.5,
        px: 1.5,
        py: 1,
        border: '1px solid',
        borderColor: theme.color.outlineVariant,
        borderRadius: theme.borderRadius.md,
        bgcolor: theme.color.neutralHover,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography
          variant='labelSmall'
          color='textSecondary'
          sx={{ minWidth: 32 }}
        >
          {t('filters.type')}
        </Typography>
        <SelectField
          label=''
          size='small'
          value={filters.type ?? ''}
          onChange={(event: { target: { value: string } }) =>
            onTypeChange(
              event.target.value
                ? (event.target.value as OptimizerRunV2Type)
                : undefined,
            )
          }
          clearable
          renderValue={(selected: string) =>
            selected ? t(`runType.${selected}`) : t('filters.allTypes')
          }
          sx={{ minWidth: 128 }}
          testId='bulk-rebalance-filter-type'
        >
          {BULK_REBALANCE_START_RUN_TYPES.map((type) => (
            <MenuItem key={type} value={type}>
              {t(`runType.${type}`)}
            </MenuItem>
          ))}
        </SelectField>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography
          variant='labelSmall'
          color='textSecondary'
          sx={{ minWidth: 36 }}
        >
          {t('filters.state')}
        </Typography>
        <SelectField
          label=''
          size='small'
          value={filters.state ?? ''}
          onChange={(event: { target: { value: string } }) =>
            onStateChange(
              event.target.value
                ? (event.target.value as OptimizerRunV2State)
                : undefined,
            )
          }
          clearable
          renderValue={(selected: string) =>
            selected ? t(`runState.${selected}`) : t('filters.allStates')
          }
          sx={{ minWidth: 136 }}
          testId='bulk-rebalance-filter-state'
        >
          {TERMINAL_RUN_STATES.map((state) => (
            <MenuItem key={state} value={state}>
              {t(`runState.${state}`)}
            </MenuItem>
          ))}
        </SelectField>
      </Box>

      {hasActiveFilters && (
        <Button
          variant='text'
          size='small'
          onClick={clearFilters}
          data-testid='bulk-rebalance-clear-filters'
        >
          {t('filters.clear')}
        </Button>
      )}
    </Box>
  );
};
